import { normalizeFreeAccess } from '@/libs/freeAccess'

// The status endpoint is the only commercial/entitlement preflight source.
// Keep normalization and its short authenticated cache here so individual
// screens cannot invent access, approval, review-window, or offer facts.

const { axiosRoutes: { subscription: subscriptionPath } } = require('../../G_CONFIG')

const statusHttpClient = () => {
  // Load lazily so the auth interceptor can invalidate the cache without
  // recursively instantiating the configured axios/JWT singleton.
  // eslint-disable-next-line global-require
  const dependency = require('@axios')
  return dependency.default || dependency
}

export const SUBSCRIPTION_STANDING_CACHE_TTL_MS = 5000
export const SUBSCRIPTION_STANDING_STALE_EVENT = 'diko:subscription-standing-stale'
export const SUBSCRIPTION_STANDING_INVALIDATED_EVENT = 'diko:subscription-standing-invalidated'

let cachedSnapshot = null
let cachedAt = 0
let inFlightRequest = null
let cacheGeneration = 0

const owns = (value, key) => Object.prototype.hasOwnProperty.call(value, key)
const isObject = value => Boolean(value) && typeof value === 'object' && !Array.isArray(value)
const isNullableString = value => value == null || typeof value === 'string'

const normalizeApprovalStanding = value => {
  if (!isObject(value)
    || typeof value.allowed !== 'boolean'
    || !owns(value, 'reason')
    || !isNullableString(value.reason)
    || !owns(value, 'approvalStatus')
    || !isNullableString(value.approvalStatus)) return null

  return {
    allowed: value.allowed,
    reason: value.reason || null,
    approvalStatus: value.approvalStatus || null,
  }
}

const normalizeApprovalReview = value => {
  if (!isObject(value)
    || typeof value.state !== 'string'
    || !owns(value, 'startedAt')
    || !isNullableString(value.startedAt)
    || !owns(value, 'deadlineAt')
    || !isNullableString(value.deadlineAt)) return null

  return {
    ...value,
    startedAt: value.startedAt || null,
    deadlineAt: value.deadlineAt || null,
  }
}

const normalizePromotion = value => {
  if (!isObject(value)
    || !owns(value, 'packageKey')
    || !isNullableString(value.packageKey)
    || typeof value.redeemed !== 'boolean') return null

  return {
    ...value,
    packageKey: value.packageKey || null,
    redeemed: value.redeemed,
  }
}

// Offer fields deliberately remain server-shaped. In particular, do not
// insert a promotional/default plan while a request is loading or failed;
// Module 05 can render only the exact offer returned by the API.
const normalizeOffer = value => (isObject(value) ? { ...value } : null)

export const normalizeStanding = data => {
  const d = isObject(data) ? data : {}
  const approvalStanding = normalizeApprovalStanding(d.approvalStanding)
  const hasAccess = typeof d.hasAccess === 'boolean' ? d.hasAccess : null
  const explicitGatedAccess = typeof d.canUseGatedFeatures === 'boolean'
    ? d.canUseGatedFeatures
    : null

  // `contractComplete` is intentionally the minimum action-guard contract,
  // not the larger lifecycle/pricing projection. approvalReview, promotion,
  // and offer are useful to other screens but are not authorization axes.
  // An internally inconsistent compatibility selector is also unsafe.
  const contractComplete = hasAccess !== null
    && owns(d, 'accessReason')
    && isNullableString(d.accessReason)
    && approvalStanding !== null
    && typeof d.accountBlocked === 'boolean'
    && explicitGatedAccess !== null
    && explicitGatedAccess === (hasAccess && approvalStanding.allowed)

  return {
    contractComplete,
    freeAccess: normalizeFreeAccess(d.freeAccess),
    hasAccess,
    accessReason: owns(d, 'accessReason') && isNullableString(d.accessReason)
      ? d.accessReason || null
      : null,
    canUseGatedFeatures: explicitGatedAccess !== null
      ? explicitGatedAccess
      : Boolean(hasAccess),
    approvalStanding,
    // Compatibility selectors for existing presentation components. The
    // shared action guard never trusts them without `contractComplete`.
    approvalAllowed: approvalStanding ? approvalStanding.allowed : true,
    approvalReason: approvalStanding ? approvalStanding.reason : null,
    approvalStatus: approvalStanding ? approvalStanding.approvalStatus : null,
    accountBlocked: typeof d.accountBlocked === 'boolean' ? d.accountBlocked : false,
    approved: typeof d.approved === 'boolean' ? d.approved : null,
    subscriptionStatus: typeof d.subscriptionStatus === 'string' ? d.subscriptionStatus : null,
    hasSubscription: typeof d.hasSubscription === 'boolean' ? d.hasSubscription : null,
    approvalReview: normalizeApprovalReview(d.approvalReview),
    promotion: normalizePromotion(d.promotion),
    offer: normalizeOffer(d.offer),
    scheduledCancellationAt: d.scheduledCancellationAt || null,
    subscriptionStartedAt: d.subscriptionStartedAt || null,
    trialEndsAt: d.trialEndsAt || null,
    pastDueSince: d.pastDueSince || null,
    graceEndsAt: d.graceEndsAt || null,
  }
}

const publishInvalidation = () => {
  if (typeof window === 'undefined'
    || typeof window.dispatchEvent !== 'function'
    || typeof CustomEvent !== 'function') return
  window.dispatchEvent(new CustomEvent(SUBSCRIPTION_STANDING_INVALIDATED_EVENT, {
    detail: { generation: cacheGeneration },
  }))
}

export const invalidateSubscriptionStanding = (options = {}) => {
  cacheGeneration += 1
  cachedSnapshot = null
  cachedAt = 0
  // The old promise may still resolve for its original caller, but it may
  // neither refill this identity's cache nor be shared by the next identity.
  inFlightRequest = null
  if (!options || options.notify !== false) publishInvalidation()
}

export function fetchSubscriptionStanding(options = {}) {
  const forceRefresh = options === true || Boolean(options && options.forceRefresh)
  const now = Date.now()

  if (!forceRefresh && cachedSnapshot && now - cachedAt < SUBSCRIPTION_STANDING_CACHE_TTL_MS) {
    return Promise.resolve(cachedSnapshot)
  }
  if (inFlightRequest) return inFlightRequest

  if (forceRefresh) {
    cachedSnapshot = null
    cachedAt = 0
  }

  const requestGeneration = cacheGeneration
  const request = statusHttpClient().get(subscriptionPath.status)
    .then(res => normalizeStanding(res && res.data))
    .then(snapshot => {
      if (requestGeneration === cacheGeneration) {
        cachedSnapshot = snapshot
        cachedAt = Date.now()
      }
      return snapshot
    })
    .finally(() => {
      if (inFlightRequest === request) inFlightRequest = null
    })

  inFlightRequest = request
  return request
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  // Authentication boundaries clear identity-scoped cache silently. Direct
  // business-state invalidations notify mounted lifecycle surfaces; those
  // surfaces defer their refresh and verify authentication after token swaps.
  window.addEventListener('diko:auth-changed', () => {
    invalidateSubscriptionStanding({ notify: false })
  })
  window.addEventListener(SUBSCRIPTION_STANDING_STALE_EVENT, () => {
    invalidateSubscriptionStanding()
  })
}

// The navbar alert is presentation-only. It fires only when subscription
// access exists but approval standing blocks starting new work.
export const approvalAlertVisible = s => Boolean(
  s && s.hasAccess === true && s.canUseGatedFeatures === false
  && ['approval-rejected', 'approval-missing'].includes(s.approvalReason),
)
